import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/shared/auth/authStore";
import { WS_DEST_USER_MESSAGES, onWsDestination, parseWsJson } from "@/shared/ws/wsBus";
import { useWsStore } from "@/shared/ws/wsStore";
import { callerLabel } from "./callerInfo";
import {
  applyServerPauseMessage,
  bootSoftphone,
  checkPendingApproval,
  handleForcedLogout,
  onPageHide,
  releaseSoftphone,
} from "./softphoneController";
import { useSoftphoneStore, type ScreenPopEntry } from "./softphoneStore";

const LOCK_NAME = "pm-softphone-owner";
const FORCED_LOGOUT = "forced logout";

/** Agente "puro" (só ROLE_USER): entra disponível automaticamente, como no softphone legado. */
export function isAgentOnly(roles: string[]): boolean {
  return roles.includes("ROLE_USER") && !roles.includes("ROLE_ADMIN") && !roles.includes("ROLE_SUPER");
}

/**
 * Ciclo de vida do softphone, montado uma vez no AppShell:
 *  - posse entre abas via Web Locks (só uma aba registra o ramal e manda heartbeat; ao fechar a
 *    dona, a próxima assume sozinha);
 *  - boot/restauração do status, desligamento no unload;
 *  - mensagens do servidor em `/user/queue/messages` (barramento da conexão STOMP única):
 *    screen pop do discador, pausa vinda do ServiceNow e logout forçado;
 *  - logout forçado também detectado pelo `/topic/phoneStatus` (sessionInfo "Forced logout");
 *  - polling da aprovação de pausa;
 *  - aviso de chamada no título da aba / notificação do sistema.
 */
export function useSoftphoneRuntime() {
  const token = useAuthStore((s) => s.token);
  const subject = useAuthStore((s) => s.tokenSubject);
  const ownership = useSoftphoneStore((s) => s.ownership);
  const pendingApproval = useSoftphoneStore((s) => s.pendingApproval);
  const navigate = useNavigate();
  const authenticated = token != null;

  // Posse entre abas.
  useEffect(() => {
    if (!authenticated || !subject) return;
    const patch = useSoftphoneStore.getState().patch;
    const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
    if (!locks?.request) {
      patch({ ownership: "this" });
      return () => patch({ ownership: "pending" });
    }
    let acquired = false;
    let disposed = false;
    let release: (() => void) | null = null;
    const ac = new AbortController();
    const waitTimer = window.setTimeout(() => {
      if (!acquired && !disposed) patch({ ownership: "other" });
    }, 500);
    locks
      .request(LOCK_NAME, { signal: ac.signal }, () => {
        acquired = true;
        if (disposed) return Promise.resolve();
        patch({ ownership: "this" });
        return new Promise<void>((resolve) => {
          release = resolve;
        });
      })
      .catch(() => {
        /* abortado */
      });
    return () => {
      disposed = true;
      window.clearTimeout(waitTimer);
      ac.abort();
      release?.();
      patch({ ownership: "pending" });
    };
  }, [authenticated, subject]);

  // Boot quando esta aba é a dona; libera ao perder a posse/desmontar.
  useEffect(() => {
    if (ownership !== "this" || !authenticated) return;
    const roles = useAuthStore.getState().roles;
    void bootSoftphone({ autoOnline: isAgentOnly(roles) });
    return () => {
      void releaseSoftphone();
    };
  }, [ownership, authenticated]);

  // Token sumiu (logout em outra aba, refresh falhou, logout forçado) → para tudo e vai ao login.
  useEffect(() => {
    if (authenticated) return;
    void releaseSoftphone().then(() => useSoftphoneStore.getState().reset());
    navigate("/login", { replace: true });
  }, [authenticated, navigate]);

  // Unload / bfcache.
  useEffect(() => {
    if (ownership !== "this") return;
    const hide = () => onPageHide();
    const show = (e: PageTransitionEvent) => {
      if (e.persisted) void bootSoftphone({ autoOnline: false });
    };
    window.addEventListener("pagehide", hide);
    window.addEventListener("pageshow", show);
    return () => {
      window.removeEventListener("pagehide", hide);
      window.removeEventListener("pageshow", show);
    };
  }, [ownership]);

  // Mensagens privadas do servidor.
  useEffect(
    () =>
      onWsDestination(WS_DEST_USER_MESSAGES, (body) => {
        const text = body.trim();
        const msg = parseWsJson(text) as Record<string, unknown> | null;
        if (!msg || typeof msg !== "object") {
          if (text.toLowerCase().includes(FORCED_LOGOUT) || /FORCE_LOGOUT/.test(text)) void handleForcedLogout();
          return;
        }
        const type = String(msg.type ?? "");
        if (type === "OUTBOUND_SCREEN_POP") {
          useSoftphoneStore.getState().patch({ screenPop: { ...(msg as ScreenPopEntry), receivedAt: Date.now() } });
          return;
        }
        if (type === "FORCE_LOGOUT" || type === "FORCED_LOGOUT") {
          void handleForcedLogout();
          return;
        }
        if ("isPaused" in msg) {
          if (useSoftphoneStore.getState().ownership === "this") {
            applyServerPauseMessage(msg as { isPaused?: boolean; breakDto?: Record<string, unknown> | null });
          }
        }
      }),
    [],
  );

  // Logout forçado visto no painel em tempo real (transição para sessionInfo "Forced logout").
  useEffect(
    () =>
      useWsStore.subscribe((state, prev) => {
        const sp = useSoftphoneStore.getState();
        const number = sp.phone?.phoneNumber;
        if (!number || sp.sessionStatus === "OFFLINE" || sp.ownership !== "this") return;
        const now = state.phones.find((p) => p.phoneNumber === number);
        const before = prev.phones.find((p) => p.phoneNumber === number);
        if (!now || !before) return;
        const isForced = (p: typeof now) =>
          String(p.sessionInfo ?? "").toLowerCase() === FORCED_LOGOUT ||
          String(p.phoneInfo ?? "").toLowerCase() === FORCED_LOGOUT;
        if (isForced(now) && !isForced(before)) void handleForcedLogout();
      }),
    [],
  );

  // Polling da aprovação de pausa.
  useEffect(() => {
    if (!pendingApproval || ownership !== "this") return;
    let stop = false;
    const id = window.setInterval(async () => {
      if (stop) return;
      if (await checkPendingApproval()) window.clearInterval(id);
    }, 5000);
    return () => {
      stop = true;
      window.clearInterval(id);
    };
  }, [pendingApproval, ownership]);

  // Chamada tocando: pisca o título e notifica se a aba estiver em segundo plano.
  useEffect(() => {
    let blink: number | null = null;
    let baseTitle = document.title;
    let notification: Notification | null = null;
    const stopBlink = () => {
      if (blink != null) {
        window.clearInterval(blink);
        blink = null;
        document.title = baseTitle;
      }
      notification?.close();
      notification = null;
    };
    const unsub = useSoftphoneStore.subscribe((s, prev) => {
      const ringing = s.call?.state === "ringing" && s.call.direction === "incoming";
      const wasRinging = prev.call?.state === "ringing" && prev.call.direction === "incoming";
      if (ringing && !wasRinging && s.call) {
        baseTitle = document.title;
        const label = callerLabel(s.call.remote);
        let on = false;
        blink = window.setInterval(() => {
          on = !on;
          document.title = on ? `Chamada: ${label}` : baseTitle;
        }, 900);
        if (document.hidden && typeof Notification !== "undefined" && Notification.permission === "granted") {
          try {
            notification = new Notification("Chamada recebida", { body: label, tag: "pm-softphone-ring" });
            notification.onclick = () => window.focus();
          } catch {
            /* ignore */
          }
        }
      } else if (!ringing && wasRinging) {
        stopBlink();
      }
    });
    return () => {
      unsub();
      stopBlink();
    };
  }, []);
}
